import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { SimulationAnalysis } from 'src/app/models/analysis/simulation-analysis.model';
import { Subscription } from 'rxjs';
import { ClustersService } from 'src/app/services/clusters.service';
import { Cluster } from 'src/app/models/cluster.model';
import { RiskManagementParameters } from 'src/app/models/risk-management-parameters.model';
import { RiskManagementService } from 'src/app/services/risk-management.service';
import { NotificationsService } from 'src/app/services/notifications.service';

@Component({
  selector: 'app-risk-management-simulation',
  templateUrl: './risk-management-simulation.component.html',
  styleUrls: ['./risk-management-simulation.component.scss']
})
export class RiskManagementSimulationComponent implements OnInit, OnDestroy {

  currentCluster: Cluster;
  currentClusterSubscription: Subscription;

  @Input() parameters: RiskManagementParameters;
  @Input() isComputing: boolean = false;
  @Input() showResults: boolean = false;

  monteCarloSimulation: SimulationAnalysis;
  monteCarloSimulationSubscription: Subscription;

  pivotSubCategories = {
    aggressiveness: {
      show_unprotected: {
        gap_risk: true,
        returns: true,
        volatility: true
      },
      columns: [
        {key:'defensive', name:'Defensive'},
        {key:'moderate', name:'Moderate'},
        {key:'aggressive', name:'Aggressive'}
      ]
    },
    maximum_loss: {
      show_unprotected: {
        gap_risk: false,
        returns: true,
        volatility: true
      },
      columns: [
        {key:'10', name:'Max Loss - 10%'},
        {key:'20', name:'Max Loss - 20%'},
        {key:'30', name:'Max Loss - 30%'}
      ]
    },
    maximum_drawdown: {
      show_unprotected: {
        gap_risk: false,
        returns: true,
        volatility: true
      },
      columns: [
        {key:'10', name:'Max Drawdown - 10%'},
        {key:'20', name:'Max Drawdown - 20%'},
        {key:'30', name:'Max Drawdown - 30%'}
      ]
    },
    protection_horizon: {
      show_unprotected: {
        gap_risk: false,
        returns: true,
        volatility: true
      },
        columns: [
        {key:'one_year', name:'1 Year'},
        {key:'three_years', name:'3 Years'}
      ]
    }
  };

  pivotChartSeries:any = {};

  selectedTab: string = 'Gap Risk';
  panelOpenState: boolean = true;

  constructor(private clustersService: ClustersService,
              private riskManagementService: RiskManagementService,
              private notificationsService: NotificationsService) {}

  ngOnInit(): void {
    this.currentClusterSubscription = this.clustersService.currentClusterSubject.subscribe(
      (currentCluster: Cluster) => {
        this.currentCluster = currentCluster;
      }
    );
    this.clustersService.emitCurrentCluster();

    // Default parameters if not specified by the parent component
    if (!this.parameters) {
      this.parameters = new RiskManagementParameters();
      this.parameters.initFromData({
        initial_capital: 100,
        aggressiveness: 'moderate',
        rebalancing_frequency: 'weekly',
        target_type: 'maximum_loss',
        protection_level: 10,
        risk_free_rate: 1,
        protection_horizon: ['one_year','three_years'][1],
        simulation_pivot: ['aggressiveness','maximum_loss','maximum_drawdown','protection_horizon'][0]
      });
    }

    this.subscribeToMonteCarloSimulation();
  }

  ngOnDestroy() {
    if(this.currentClusterSubscription!=undefined) { this.currentClusterSubscription.unsubscribe(); }
    if(this.monteCarloSimulationSubscription!=undefined) { this.monteCarloSimulationSubscription.unsubscribe(); }
  }

  subscribeToMonteCarloSimulation() {
    this.monteCarloSimulationSubscription = this.riskManagementService.monteCarloSimulationSubject.subscribe(
      (simulation_analysis: SimulationAnalysis) => {
        if (simulation_analysis) {
          this.defineChartSeries(simulation_analysis);
          this.monteCarloSimulation = simulation_analysis;
        }
      }
    );
    this.riskManagementService.emitMonteCarloSimulation();
  }

  onTabChange(event: MatTabChangeEvent) {
    switch (event.tab.textLabel) {
      case 'Gap Risk':   this.selectedTab='Gap Risk'; break;
      case 'Returns':    this.selectedTab='Returns'; break;
      case 'Volatility': this.selectedTab='Volatility'; break;
    }
  }

  defineChartSeries(simulation_analysis: SimulationAnalysis) {
    const is_aggressiveness: boolean = this.parameters.simulation_pivot=='aggressiveness',
          is_maximum_loss: boolean = this.parameters.simulation_pivot=='maximum_loss',
          is_maximum_drawdown: boolean = this.parameters.simulation_pivot=='maximum_drawdown',
          is_protection_horizon: boolean = this.parameters.simulation_pivot=='protection_horizon';
    this.pivotChartSeries = {
      aggressiveness: is_aggressiveness ? {
        returns: [
          {label: 'defensive', values: simulation_analysis.aggressiveness.defensive.returns.density},
          {label: 'moderate', values: simulation_analysis.aggressiveness.moderate.returns.density},
          {label: 'aggressive', values: simulation_analysis.aggressiveness.aggressive.returns.density},
          {label: 'unprotected', values: simulation_analysis.unprotected.returns.density}
        ],
        volatility: [
          {label: 'defensive', values: simulation_analysis.aggressiveness.defensive.volatility.density},
          {label: 'moderate', values: simulation_analysis.aggressiveness.moderate.volatility.density},
          {label: 'aggressive', values: simulation_analysis.aggressiveness.aggressive.volatility.density},
          {label: 'unprotected', values: simulation_analysis.unprotected.volatility.density}
        ],
        gap_risk: [
          {label: 'defensive', values: simulation_analysis.aggressiveness.defensive.gap_risk},
          {label: 'moderate', values: simulation_analysis.aggressiveness.moderate.gap_risk},
          {label: 'aggressive', values: simulation_analysis.aggressiveness.aggressive.gap_risk},
          {label: 'unprotected', values: simulation_analysis.unprotected.gap_risk}
        ]
      } : undefined,
      maximum_loss: is_maximum_loss ? {
        returns: [
          {label: 'maximum_loss_10', values: simulation_analysis.maximum_loss['10'].returns.density},
          {label: 'maximum_loss_20', values: simulation_analysis.maximum_loss['20'].returns.density},
          {label: 'maximum_loss_30', values: simulation_analysis.maximum_loss['30'].returns.density},
          {label: 'unprotected', values: simulation_analysis.unprotected.returns.density}
        ],
        volatility: [
          {label: 'maximum_loss_10', values: simulation_analysis.maximum_loss['10'].volatility.density},
          {label: 'maximum_loss_20', values: simulation_analysis.maximum_loss['20'].volatility.density},
          {label: 'maximum_loss_30', values: simulation_analysis.maximum_loss['30'].volatility.density},
          {label: 'unprotected', values: simulation_analysis.unprotected.volatility.density}
        ],
        gap_risk: [
          {label: 'maximum_loss_10', values: simulation_analysis.maximum_loss['10'].gap_risk},
          {label: 'maximum_loss_20', values: simulation_analysis.maximum_loss['20'].gap_risk},
          {label: 'maximum_loss_30', values: simulation_analysis.maximum_loss['30'].gap_risk}
        ]
      } : undefined,
      maximum_drawdown: is_maximum_drawdown ? {
        returns: [
          {label: 'maximum_drawdown_10', values: simulation_analysis.maximum_drawdown['10'].returns.density},
          {label: 'maximum_drawdown_20', values: simulation_analysis.maximum_drawdown['20'].returns.density},
          {label: 'maximum_drawdown_30', values: simulation_analysis.maximum_drawdown['30'].returns.density},
          {label: 'unprotected', values: simulation_analysis.unprotected.returns.density}
        ],
        volatility: [
          {label: 'maximum_drawdown_10', values: simulation_analysis.maximum_drawdown['10'].volatility.density},
          {label: 'maximum_drawdown_20', values: simulation_analysis.maximum_drawdown['20'].volatility.density},
          {label: 'maximum_drawdown_30', values: simulation_analysis.maximum_drawdown['30'].volatility.density},
          {label: 'unprotected', values: simulation_analysis.unprotected.volatility.density}
        ],
        gap_risk: [
          {label: 'maximum_drawdown_10', values: simulation_analysis.maximum_drawdown['10'].gap_risk},
          {label: 'maximum_drawdown_20', values: simulation_analysis.maximum_drawdown['20'].gap_risk},
          {label: 'maximum_drawdown_30', values: simulation_analysis.maximum_drawdown['30'].gap_risk}
        ]
      } : undefined,
      protection_horizon: is_protection_horizon ? {
        returns: [
          {label: 'one_year', values: simulation_analysis.protection_horizon.one_year.returns.density},
          {label: 'three_years', values: simulation_analysis.protection_horizon.three_years.returns.density},
          {label: 'unprotected', values: simulation_analysis.unprotected.returns.density}
        ],
        volatility: [
          {label: 'one_year', values: simulation_analysis.protection_horizon.one_year.volatility.density},
          {label: 'three_years', values: simulation_analysis.protection_horizon.three_years.volatility.density},
          {label: 'unprotected', values: simulation_analysis.unprotected.volatility.density}
        ],
        gap_risk: [
          {label: 'one_year', values: simulation_analysis.protection_horizon.one_year.gap_risk},
          {label: 'three_years', values: simulation_analysis.protection_horizon.three_years.gap_risk}
        ]
      } : undefined
    };
  }

  async onCompute() {
    this.notificationsService.runSpinner();
    this.isComputing = true;
    this.showResults = false;

    try {
      await this.riskManagementService.computeMonteCarlo(this.parameters, this.currentCluster);
      this.notificationsService.notify('computation-success');
      this.showResults = true;
    }
    catch(e) {
      this.notificationsService.notify('computation-failed');
      this.showResults = false;
    }

    this.selectedTab = 'Gap Risk';

    this.isComputing = false;
    this.panelOpenState = false;
    this.notificationsService.stopSpinner();
  }
}
